import * as i0 from '@angular/core';
import { EventEmitter, Directive, Input, Output, HostListener, NgModule } from '@angular/core';

class ReadFileImpl {
    get name() {
        return this.underlyingFile.name;
    }
    get size() {
        return this.underlyingFile.size;
    }
    get type() {
        return this.underlyingFile.type;
    }
    constructor(underlyingFile, readMode, content) {
        this.underlyingFile = underlyingFile;
        this.readMode = readMode;
        this.content = content;
    }
}

var ReadMode;
(function (ReadMode) {
    ReadMode[ReadMode["ArrayBuffer"] = 0] = "ArrayBuffer";
    ReadMode[ReadMode["BinaryString"] = 1] = "BinaryString";
    ReadMode[ReadMode["DataURL"] = 2] = "DataURL";
    ReadMode[ReadMode["Text"] = 3] = "Text";
    ReadMode[ReadMode["Skip"] = 4] = "Skip";
})(ReadMode || (ReadMode = {}));

function coerceBooleanProperty(value) {
    return value != null && `${value}` !== 'false';
}
async function readFileAsync(file, readMode) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const fileReader = event.target;
            const readFile = new ReadFileImpl(file, readMode, fileReader.result);
            resolve(readFile);
        };
        reader.onerror = (event) => {
            reject(event);
        };
        switch (readMode) {
            case ReadMode.ArrayBuffer:
                reader.readAsArrayBuffer(file);
                break;
            case ReadMode.BinaryString:
                reader.readAsBinaryString(file);
                break;
            case ReadMode.Text:
                reader.readAsText(file);
                break;
            case ReadMode.Skip:
                // Immediately return without reading the file
                // See: https://github.com/fvilers/ngx-file-helpers/issues/57
                resolve(new ReadFileImpl(file, readMode));
                break;
            case ReadMode.DataURL:
            default:
                reader.readAsDataURL(file);
                break;
        }
    });
}

class FileHandler {
    constructor() {
        this.readMode = ReadMode.DataURL;
        this.filter = () => true;
        this.readStart = new EventEmitter();
        this.readEnd = new EventEmitter();
        this.readError = new EventEmitter();
    }
    async readFiles(files, onFileRead) {
        const filteredFiles = Array.from(files).filter((file, index, array) => this.filter(file, index, array));
        const fileCount = filteredFiles.length;
        let readCount = 0;
        this.readStart.emit(fileCount);
        await Promise.all(filteredFiles.map(async (file) => {
            try {
                const readFile = await readFileAsync(file, this.readMode);
                onFileRead(readFile);
                readCount++;
            }
            catch (err) {
                this.readError.emit({ file, error: err });
                // do not re-throw, the promise returned by readFiles is not awaited anywhere
                // and re-throwing would result in "unhandled rejections" that the consumer cannot handle
            }
        })).finally(() => {
            this.readEnd.emit(readCount);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.5", ngImport: i0, type: FileHandler, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.5", type: FileHandler, inputs: { readMode: "readMode", filter: "filter" }, outputs: { readStart: "readStart", readEnd: "readEnd", readError: "readError" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.5", ngImport: i0, type: FileHandler, decorators: [{
            type: Directive
        }], propDecorators: { readMode: [{
                type: Input
            }], filter: [{
                type: Input
            }], readStart: [{
                type: Output
            }], readEnd: [{
                type: Output
            }], readError: [{
                type: Output
            }] } });

class FileDropzoneDirective extends FileHandler {
    constructor() {
        super(...arguments);
        this.fileDrop = new EventEmitter();
    }
    onDragEnter(event) {
        event.stopPropagation();
        event.preventDefault();
    }
    onDragOver(event) {
        event.stopPropagation();
        event.preventDefault();
    }
    onDrop(event) {
        event.stopPropagation();
        event.preventDefault();
        if (event.dataTransfer !== null) {
            this.readFiles(event.dataTransfer.files, (readFile) => this.fileDrop.emit(readFile));
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.5", ngImport: i0, type: FileDropzoneDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.5", type: FileDropzoneDirective, selector: "[ngxFileDropzone]", outputs: { fileDrop: "fileDrop" }, host: { listeners: { "dragenter": "onDragEnter($event)", "dragover": "onDragOver($event)", "drop": "onDrop($event)" } }, exportAs: ["ngxFileDropzone"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.5", ngImport: i0, type: FileDropzoneDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxFileDropzone]',
                    exportAs: 'ngxFileDropzone',
                }]
        }], propDecorators: { fileDrop: [{
                type: Output
            }], onDragEnter: [{
                type: HostListener,
                args: ['dragenter', ['$event']]
            }], onDragOver: [{
                type: HostListener,
                args: ['dragover', ['$event']]
            }], onDrop: [{
                type: HostListener,
                args: ['drop', ['$event']]
            }] } });

class FilePickerDirective extends FileHandler {
    get multiple() {
        return this._multiple;
    }
    set multiple(value) {
        this._multiple = coerceBooleanProperty(value);
    }
    constructor(el, renderer) {
        super();
        this.el = el;
        this.renderer = renderer;
        this.accept = '';
        this._multiple = false;
        this.filePick = new EventEmitter();
    }
    ngOnInit() {
        this._input = this.renderer.createElement('input');
        this.renderer.appendChild(this.el.nativeElement, this._input);
        this.renderer.setAttribute(this._input, 'type', 'file');
        this.renderer.setAttribute(this._input, 'accept', this.accept);
        this.renderer.setStyle(this._input, 'display', 'none');
        if (this.multiple) {
            this.renderer.setAttribute(this._input, 'multiple', 'multiple');
        }
        this.renderer.listen(this._input, 'change', (event) => this._onListen(event));
    }
    reset() {
        if (!this._input) {
            console.error('It seems that ngOnInit() has not been executed or that the hidden _input element is null. Did you mess with the DOM?');
            return;
        }
        this._input.value = null;
    }
    browse() {
        if (!this._input) {
            console.error('It seems that ngOnInit() has not been executed or that the hidden _input element is null. Did you mess with the DOM?');
            return;
        }
        this._input.click();
    }
    // The callback signature prevent the async/await usage
    _onListen(event) {
        const target = event.target;
        if (target.files !== null) {
            this.readFiles(target.files, (readFile) => this.filePick.emit(readFile))
                // reset value to trick change event making it changeable every time
                .finally(() => (target.value = ''));
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.5", ngImport: i0, type: FilePickerDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.0.5", type: FilePickerDirective, selector: "[ngxFilePicker]", inputs: { accept: "accept", multiple: "multiple" }, outputs: { filePick: "filePick" }, host: { listeners: { "click": "browse()" } }, exportAs: ["ngxFilePicker"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.5", ngImport: i0, type: FilePickerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxFilePicker]',
                    exportAs: 'ngxFilePicker',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { accept: [{
                type: Input
            }], multiple: [{
                type: Input
            }], filePick: [{
                type: Output
            }], browse: [{
                type: HostListener,
                args: ['click']
            }] } });

class NgxFileHelpersModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.0.5", ngImport: i0, type: NgxFileHelpersModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.0.5", ngImport: i0, type: NgxFileHelpersModule, declarations: [FileDropzoneDirective, FilePickerDirective], exports: [FileDropzoneDirective, FilePickerDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.0.5", ngImport: i0, type: NgxFileHelpersModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.0.5", ngImport: i0, type: NgxFileHelpersModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FileDropzoneDirective, FilePickerDirective],
                    imports: [],
                    exports: [FileDropzoneDirective, FilePickerDirective],
                }]
        }] });

/*
 * Public API Surface of ngx-file-helpers
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FileDropzoneDirective, FilePickerDirective, NgxFileHelpersModule, ReadMode };
